import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { loginUser, registerUser, resetPassword } from 'api/auth'
import { assignGuestChatsToCurrentUser, getMyChats } from 'api/chats'
import { showErrorToast, showSuccessToast } from 'utils/toast'
import { getUserNameSurname } from 'utils/user'
import { analytics, ANALYTICS_EVENTS } from 'services/analytics'

export type LoginFormValues = {
  email: string
  password: string
}

export type RegisterFormValues = {
  email: string
  password: string
  name: string
  surname: string
}

export type ResetPasswordFormValues = {
  email: string
}

interface UseAuthFlowReturn {
  handleLogin: (values: LoginFormValues, onSuccess?: () => void) => Promise<void>
  handleRegister: (values: RegisterFormValues, onSuccess?: () => void) => Promise<void>
  handleResetPassword: (values: ResetPasswordFormValues, onSuccess?: () => void) => Promise<void>
  isLoading: boolean
}

/**
 * Hook to handle authentication flows (login, register, reset password).
 * Encapsulates common logic:
 * - API calls
 * - Guest chat assignment
 * - Success/error toasts
 */
export const useAuthFlow = (): UseAuthFlowReturn => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  const handleLogin = useCallback(
    async (values: LoginFormValues, onSuccess?: () => void) => {
      try {
        setIsLoading(true)
        const user = await loginUser(values.email, values.password)
        analytics.track(ANALYTICS_EVENTS.LOGIN)
        await assignGuestChatsToCurrentUser()
        await getMyChats({})
        const nameSurname = getUserNameSurname(user)
        showSuccessToast(`${t('welcomeLogin')} ${nameSurname}`)
        onSuccess?.()
      } catch (e) {
        console.error({ e })
        if (e === 'userNotAllowedToLogin') {
          showErrorToast(t('userNotAllowedToLogin'))
        } else {
          showErrorToast(t('loginError'))
        }
        throw e
      } finally {
        setIsLoading(false)
      }
    },
    [t],
  )

  const handleRegister = useCallback(
    async (values: RegisterFormValues, onSuccess?: () => void) => {
      try {
        setIsLoading(true)
        const user = await registerUser(values)
        analytics.track(ANALYTICS_EVENTS.SIGN_UP)
        await assignGuestChatsToCurrentUser()
        await getMyChats({})
        const nameSurname = getUserNameSurname(user)
        showSuccessToast(`${t('welcomeLogin')} ${nameSurname}`)
        onSuccess?.()
      } catch (e) {
        console.error({ e })
        if (e === 'userNotAllowedToLogin') {
          showErrorToast(t('userNotAllowedToLogin'))
        } else {
          showErrorToast(t('loginError'))
        }
        throw e
      } finally {
        setIsLoading(false)
      }
    },
    [t],
  )

  const handleResetPassword = useCallback(
    async (values: ResetPasswordFormValues, onSuccess?: () => void) => {
      try {
        setIsLoading(true)
        await resetPassword(values.email)
        showSuccessToast(t('resetPasswordEmailSend'))
        onSuccess?.()
      } catch (e) {
        console.error({ e })
        throw e
      } finally {
        setIsLoading(false)
      }
    },
    [t],
  )

  return {
    handleLogin,
    handleRegister,
    handleResetPassword,
    isLoading,
  }
}
